import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { Language, getTranslations } from '@/lib/i18n';

interface RestaurantStoryProps {
  language: Language;
}

const valueIcons = ['MapPinIcon', 'HeartIcon', 'GlobeAltIcon', 'UserGroupIcon'];

export default function RestaurantStory({ language }: RestaurantStoryProps) {
  const t = getTranslations(language);
  const s = t.home.story;

  return (
    <section className="py-24 md:py-32 px-6 md:px-12 bg-muted/20 border-t border-border/50">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Image Side */}
          <div className="relative animate-fade-in">
            <div className="image-zoom-container aspect-[4/5] rounded-[40px] shadow-2xl">
              <AppImage
                src="https://images.unsplash.com/photo-1672870634122-6ea7b16d2bb4"
                alt="Warm restaurant interior with elegant table settings, ambient lighting, and modern decor"
                className="w-full h-full object-cover" />
            </div>
            {/* Floating Stats Card */}
            <div className="absolute -bottom-12 -right-12 bg-card p-8 rounded-[30px] shadow-2xl hidden lg:block border border-border/50">
              <span className="text-5xl font-serif font-bold text-primary block mb-2">8+</span>
              <p className="text-xs text-muted-foreground uppercase tracking-widest font-bold">
                {s.yearsLabel}
              </p>
            </div>
          </div>

          {/* Content Side */}
          <div className="space-y-10 animate-slide-up delay-200">
            <span className="text-accent text-xs font-bold tracking-[0.3em] uppercase block">
              {s.sectionLabel}
            </span>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.1] text-foreground">
              {s.title} <br />
              <span className="italic font-light">{s.titleItalic}</span>
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
              {s.description}
            </p>

            {/* Values Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 pt-8">
              {s.values.map((value, index) => (
                <div key={`value_${index}`} className="space-y-4 group">
                  <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-all">
                    <Icon name={valueIcons[index] as any} size={24} />
                  </div>
                  <h4 className="font-bold text-base uppercase tracking-widest text-foreground">
                    {value.title}
                  </h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}